import type {BuildUrlOptions, Url} from '@pexip/router';
import {
    buildUrl,
    createPathMatch,
    createRouter,
    createRouterHooks,
    formatPath,
} from '@pexip/router';
import {createSignal} from '@pexip/signal';

import {
    EXPRESS_PATH,
    MEETING_MANAGER_PATH,
    STEP_BY_STEP_PATH,
} from './constants';
import {logger} from './logger';
import {validateDisconnectDestination} from './utils/validateDisconnectDestination';

const baseUri = new URL(document.baseURI).pathname;

const urlRequestSignal = createSignal<Url>({name: 'router:urlRequest'});
const urlChangedSignal = createSignal<Url>({name: 'router:urlChanged'});

export const router = createRouter({
    baseUri,
    urlRequestSignal,
    urlChangedSignal,
});

export const {useRouter, createRoutesHook, createSubRoutesHook} =
    createRouterHooks(router);

export const push = (
    url: Url | string,
    {base = baseUri, ...rest}: BuildUrlOptions = {},
) => router.history.push(url, {base, ...rest});

export const replace = (
    url: Url | string,
    {base = baseUri, ...rest}: BuildUrlOptions = {},
) => router.history.replace(url, {base, ...rest});

const IN_MEETING_RE = /^\/m\/[^/]+(\/?|\/?\?[^/]+\/?)$/;

/**
 * Formats the express flow entry route of a bare meeting URL, `/m/<alias>`
 * with an optional query.
 *
 * @param url - URL to open.
 * @returns The entry route relative to the application base, or `undefined`
 * when the URL is not a bare meeting URL.
 *
 * @example
 * toMeetingEntryRoute(buildUrl('/m/meet.bob?pin=1234'));
 * // '/m/meet.bob/express?pin=1234'
 */
export const toMeetingEntryRoute = (url: Url): string | undefined => {
    const fromBase = url
        .toString()
        .slice(document.baseURI.replace(/\/$/, '').length);
    if (!IN_MEETING_RE.exec(fromBase)) {
        return undefined;
    }
    if (url.query) {
        return fromBase.replace(/\?|\/\?/, `/${EXPRESS_PATH}?`);
    }
    return `${fromBase.replace(/\/$/, '')}/${EXPRESS_PATH}`;
};

/**
 * Replaces a bare meeting URL, `/m/<alias>` with an optional query, with its
 * express flow entry route, `/m/<alias>/express`.
 *
 * @example
 * // At /m/meet.bob?pin=1234
 * redirectInMeeting(); // now at /m/meet.bob/express?pin=1234
 */
export const redirectInMeeting = () => {
    const entryRoute = toMeetingEntryRoute(buildUrl(window.location.href));
    if (entryRoute) {
        replace(entryRoute);
    }
};

export const navigateToDisconnectDestination = (
    disconnectDestination: string,
    fallbackRedirect: () => void,
) => {
    const result = validateDisconnectDestination(disconnectDestination);
    if (result.isValid) {
        window.location.href = result.url.href;
    } else {
        logger.error(result.reason);
        fallbackRedirect();
    }
};

export const navigateTo = (path: string, disconnectDestination?: string) => {
    if (disconnectDestination) {
        navigateToDisconnectDestination(disconnectDestination, () =>
            push(path),
        );
    } else {
        push(path);
    }
};

export const navigateToPostMeeting = (
    conferenceAlias: string,
    disconnectDestination?: string,
) => {
    navigateTo(
        formatPath`/m/${conferenceAlias}/post-meeting`,
        disconnectDestination,
    );
};

/**
 * Extracts the conference alias from a URL path matching the `/m/<alias>` pattern.
 *
 * @param path - A URL path, e.g. `/m/meet.bob/express` or `m/meet.bob/`
 * @returns The decoded conference alias, or `undefined` if no alias is found
 *
 * @example
 * getConferenceAliasFromPath('/m/meet.bob/express'); // 'meet.bob'
 */
export const getConferenceAliasFromPath = (
    path: string,
): string | undefined => {
    const normalized = path.startsWith('/') ? path : `/${path}`;
    const matchPath = createPathMatch(normalized);
    const {
        params: {id},
    } = matchPath('/m/:id');
    return id ?? undefined;
};

/**
 * Extracts the conference alias and flow from a meeting entry URL path.
 * Only returns values for valid entry paths (`/m/<alias>`,
 * `/m/<alias>/express`, `/m/<alias>/step-by-step`, or `/m/<alias>/mm`).
 * Returns `[undefined, undefined]` for non-entry paths like `/m/<alias>/post-meeting`.
 *
 * @param path - A URL path relative to baseUri
 * @returns A tuple of `[alias, flow]`
 *
 * @example
 * parseMeetingEntryPath('/m/meet.bob/step-by-step'); // ['meet.bob', 'step-by-step']
 * parseMeetingEntryPath('/m/meet.bob'); // ['meet.bob', undefined]
 * parseMeetingEntryPath('/m/meet.bob/post-meeting'); // [undefined, undefined]
 */
export const parseMeetingEntryPath = (
    path: string,
): [alias: string | undefined, flow: string | undefined] => {
    const normalized = path.startsWith('/') ? path : `/${path}`;
    const matchPath = createPathMatch(normalized);

    const {
        params: {id: alias},
    } = matchPath('/m/:id');
    if (!alias) {
        return [undefined, undefined];
    }

    const {
        params: {flow},
    } = matchPath('/m/:id/:flow');

    // No trailing segment — bare /m/<alias>
    if (!flow) {
        return [alias, undefined];
    }

    // Valid entry flow
    if (
        flow === EXPRESS_PATH ||
        flow === STEP_BY_STEP_PATH ||
        flow === MEETING_MANAGER_PATH
    ) {
        return [alias, flow];
    }

    // Non-entry path (e.g. /m/<alias>/post-meeting)
    return [undefined, undefined];
};
